import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import TopNav from './TopNav';
import Footer from './Footer';
import useSEO from '../hooks/useSEO';

const APP_MODEL_NEW_URL = 'https://app.veydra.io/model/new';

const workflowSteps = [
  {
    title: 'AI Conversation',
    detail: 'Describe your system and assumptions in plain language.',
  },
  {
    title: 'Generated Model',
    detail: 'Veydra drafts a dynamic model with structure and causal logic.',
  },
  {
    title: 'Editable Python',
    detail: 'Refine equations in native Python with Git, notebooks, and VS Code.',
  },
  {
    title: 'Interactive Simulation',
    detail: 'Run scenarios, compare tradeoffs, and explain consequences.',
  },
];

const veydraCards = [
  {
    title: 'AI-native modeling',
    text: 'Build models collaboratively with an AI modeling partner that understands dynamic systems.',
  },
  {
    title: 'Native Python',
    text: 'Use ordinary Python with your existing technical workflow, not a proprietary language.',
  },
  {
    title: 'Bidirectional modeling',
    text: 'Code, diagram, and AI stay synchronized in one model.',
  },
  {
    title: 'Multiple paradigms',
    text: 'System dynamics, agent-based modeling, discrete event simulation, and hybrid models.',
  },
];

const applications = [
  { title: 'Strategic Planning', to: '/solutions' },
  { title: 'Capacity Planning', to: '/solutions/workforce' },
  { title: 'Supply Chains', to: '/solutions/supply-chain' },
  { title: 'Healthcare', to: '/solutions/hospital-or' },
  { title: 'Public Policy', to: '/solutions/public-policy' },
  { title: 'Research', to: '/innovation' },
  { title: 'Education', to: '/educators' },
  { title: 'Digital Twins', to: '/solutions' },
];

const partnerPlaceholders = [
  'University of Warwick',
  'Boston University',
  'Bucknell University',
  'New York University',
  'University of Alabama in Huntsville',
  'University of Greenwich',
  'University of Indianapolis',
  'North Carolina State University',
  'Williams College',
];

const liveFeed = [
  {
    headline: 'Crude Oil Spikes +12%',
    module: 'Sourcing',
    effect: 'Supplier cost index increased by 2.4 points',
  },
  {
    headline: 'Port Strike Escalates',
    module: 'Logistics',
    effect: 'Lead-time assumptions shifted +18 days',
  },
  {
    headline: 'Fed Rate Hike Surprise',
    module: 'Finance',
    effect: 'Debt stress test activated, bond yields up',
  },
  {
    headline: 'Consumer Sentiment Dips',
    module: 'Marketing',
    effect: 'Demand elasticity shifted -0.8 coefficient',
  },
  {
    headline: 'Tech Talent War Intensifies',
    module: 'Human Capital',
    effect: 'Retention risk flagged, training ROI recalculated',
  },
  {
    headline: 'EU Carbon Tax Enacted',
    module: 'Operations',
    effect: 'Scope 3 emissions penalty triggered, supply chain audit required',
  },
  {
    headline: 'Wildfire Smoke Disrupts Operations',
    module: 'Environmental',
    effect: 'Air quality alerts tighten staffing plans and logistics windows',
  },
];

const liveTickerRows = [
  [
    'Crude Oil Spikes +12%', 'Chip Shortage Deepens', 'Warehouse Labor +22%',
    'Tariff Dispute Escalates', 'Steel Prices Surge +9%', 'Freight Rates Collapse',
    'OPEC Meeting Delayed', 'Currency Devaluation Alert', 'Auto Sales Slump 8%', 'Rare Earth Shortage',
  ],
  [
    'Port Strike Escalates', 'Supply Chain Bottleneck Worsens', 'Bond Yields Climb 3.8%',
    'OPEC Cuts Output Again', 'Rare Earth Export Ban', 'Robotics Costs Drop 15%',
    'Union Negotiations Stall', 'ESG Mandate Approved', 'Lithium Demand Doubles',
    'Factory Utilization at 94%', 'Shipping Delays +12 Days', 'Import Duties Raised',
  ],
  [
    'Fed Rate Hike Surprise', 'Tech Talent War Intensifies', 'Inflation Hits 4.2%',
    'Central Bank Holds Rates', 'EV Demand Surges 28%', 'Copper Futures Rally',
    'Cybersecurity Breach Reported', 'Retail Foot Traffic Down 11%', 'Green Bond Issuance Record',
    'Semiconductor Fab Approved', 'Trade Agreement Signed', 'IPO Market Rebounds',
  ],
  [
    'Consumer Sentiment Dips', 'Labor Market Tightens', 'AI Adoption Accelerates',
    'Emerging Market Currency Drop', 'Drought Hits Grain Supply', 'Natural Gas Storage Low',
    'New Tariff Round Announced', 'Consumer Confidence Falls', 'Ad Spend Shifts to Digital',
    'Warehouse Automation +30%', 'Housing Starts Decline', 'Rail Strike Looms',
  ],
  [
    'EU Carbon Tax Enacted', 'Container Shortage Persists', 'Brand Sentiment Dips 6pts',
    'R&D Tax Credit Extended', 'Oil Inventories Drawdown', 'Minimum Wage Increase Passed',
    'Quarterly Earnings Miss', 'Solar Panel Costs Drop 18%', 'FX Hedging Costs Rise',
    'Supply Contract Renegotiated', 'Market Share Shifts in Q3', 'Logistics Costs Spike',
  ],
];

const SCENARIO_AUTOPLAY_LEVELS = [1.2, 1.35, 1.5, 1.65, 1.8];

function LoginScreen({ logo }) {
  const [activeHeadline, setActiveHeadline] = useState(liveFeed[0].headline);
  const [hoveredHeadline, setHoveredHeadline] = useState(null);
  const [scenarioCompression, setScenarioCompression] = useState(SCENARIO_AUTOPLAY_LEVELS[0]);
  const [scenarioCustA, setScenarioCustA] = useState(true);
  const [scenarioCustB, setScenarioCustB] = useState(true);
  const [scenarioCustC, setScenarioCustC] = useState(true);
  const [scenarioAutoplay, setScenarioAutoplay] = useState(true);
  const plannerCanvasRef = useRef(null);
  const revenueCanvasRef = useRef(null);
  const plannerTimerRef = useRef(0);
  const plannerAutoIndexRef = useRef(0);
  const plannerAutoDirectionRef = useRef(1);
  const plannerAutoValueRef = useRef(SCENARIO_AUTOPLAY_LEVELS[0]);
  const plannerHoverReadyRef = useRef(false);
  const revenueAxisMaxRef = useRef(0);
  const revenueBaselineRef = useRef(null);
  const activeHeadlineRef = useRef(liveFeed[0].headline);
  const hoveredHeadlineRef = useRef(null);
  const tickerRef = useRef(null);
  const tickerState = useRef({
    positions: [0, 0, 0, 0, 0],
    baseSpeeds: [0.6, 0.35, 0.8, 0.45, 0.55],
    scrollBoost: 0,
    lastScroll: 0,
    rafId: 0,
    linkedCached: false,
    linkedTickers: [],
    linkedFeeds: [],
    activeHL: null,
  });

  const appBaseUrl = useMemo(() => {
    if (typeof window === 'undefined') {
      return 'https://app.veydra.io';
    }

    const { hostname } = window.location;
    if (
      hostname === 'localhost' ||
      hostname === '127.0.0.1' ||
      hostname.endsWith('.localhost') ||
      hostname === 'dev' ||
      hostname.endsWith('.dev')
    ) {
      return 'http://localhost:3000';
    }

    return 'https://app.veydra.io';
  }, []);

  const feedHeadlines = useMemo(() => new Set(liveFeed.map((f) => f.headline)), []);
  const activePreview = useMemo(
    () => liveFeed.find((item) => item.headline === (hoveredHeadline || activeHeadline)) || liveFeed[0],
    [activeHeadline, hoveredHeadline],
  );

  useSEO({
    title: 'AI-Native Simulation Platform',
    description:
      'Build simulation models, test strategies, and understand consequences before acting with Veydra.',
  });

  useEffect(() => {
    const capacityCanvas = plannerCanvasRef.current;
    const revenueCanvas = revenueCanvasRef.current;
    if (!capacityCanvas || !revenueCanvas) {
      return;
    }

    const capacityCtx = capacityCanvas.getContext('2d');
    const revenueCtx = revenueCanvas.getContext('2d');
    if (!capacityCtx || !revenueCtx) {
      return;
    }

    const width = capacityCanvas.width;
    const height = capacityCanvas.height;
    const paddingBottom = 25;
    const paddingTop = 25;
    const chartHeight = height - paddingBottom - paddingTop;
    const steps = 26;
    const q2BoundaryPct = 0.33;
    const maxCapacity = 65;
    const preQ2Capacity = maxCapacity * 0.8;
    const postQ2Capacity = maxCapacity * 1.2;
    const breachBuffer = 1.5;

    const clamp = (value, min, max) => Math.max(min, Math.min(max, value));
    const quantize = (value, size) => Math.round(value / size) * size;
    const noise = (index, seed) => {
      const raw = Math.sin((index + 1) * 12.9898 + seed * 78.233) * 43758.5453;
      return (raw - Math.floor(raw)) - 0.5;
    };

    const totalCurve = new Array(steps).fill(0);
    const layer1 = new Array(steps).fill(0);
    const layer2 = new Array(steps).fill(0);
    const layer3 = new Array(steps).fill(0);
    const customerCSeries = new Array(steps).fill(0);
    const revenueCumulative = new Array(steps).fill(0);
    const capacitySeries = new Array(steps).fill(0);

    const compressionLevel = clamp(scenarioCompression - 1, 0, 1);
    let runningRevenue = 0;

    if (scenarioCustC) {
      const start = 0.32;
      const activeSpan = clamp(0.76 - (compressionLevel * 0.5), 0.24, 0.76);
      const rampUp = activeSpan * 0.2;
      const plateau = activeSpan * 0.45;
      const rampDown = activeSpan * 0.35;
      const targetArea = 12.75;
      const shapeAreaFactor = 0.725;
      const heightSpike = Math.min(targetArea / (activeSpan * shapeAreaFactor), 95);

      for (let i = 0; i < steps; i += 1) {
        const xPct = i / (steps - 1);
        let valC = 0;

        if (xPct >= start && xPct < start + rampUp) {
          valC = heightSpike * ((xPct - start) / rampUp);
        } else if (xPct >= start + rampUp && xPct < start + rampUp + plateau) {
          valC = heightSpike;
        } else if (xPct >= start + rampUp + plateau && xPct < start + rampUp + plateau + rampDown) {
          valC = heightSpike * (1 - ((xPct - (start + rampUp + plateau)) / rampDown));
        }

        valC += noise(i, 3.3) * (Math.max(0.8, heightSpike * 0.06));
        customerCSeries[i] = clamp(valC, 0, 95);
      }

      const targetDiscreteVolume = targetArea * (steps - 1);
      const observedDiscreteVolume = customerCSeries.reduce((sum, v) => sum + v, 0);
      if (observedDiscreteVolume > 0) {
        const volumeScale = targetDiscreteVolume / observedDiscreteVolume;
        for (let i = 0; i < steps; i += 1) {
          customerCSeries[i] = clamp(customerCSeries[i] * volumeScale, 0, 95);
        }
      }
    }

    for (let i = 0; i < steps; i += 1) {
      const xPct = i / (steps - 1);

      let valA = 0;
      if (scenarioCustA) {
        if (xPct < 0.22) {
          valA = 32;
        } else {
          valA = 32 * (1 - ((xPct - 0.22) / 0.73));
        }
        valA += noise(i, 1.1) * 3;
        valA = clamp(quantize(valA, 0.8), 0, 42);
      }

      let valB = 0;
      if (scenarioCustB) {
        if (xPct >= 0.12 && xPct < 0.32) {
          valB = ((xPct - 0.12) / 0.2) * 14;
        } else if (xPct >= 0.32 && xPct < 0.68) {
          valB = 14 + ((0.68 - xPct) / 0.36) * 2;
        } else if (xPct >= 0.68 && xPct < 0.9) {
          valB = ((0.9 - xPct) / 0.22) * 14;
        }
        valB += noise(i, 2.2) * 2.5;
        valB = clamp(quantize(valB, 0.8), 0, 24);
      }

      const valC = scenarioCustC ? customerCSeries[i] : 0;

      layer1[i] = valA;
      layer2[i] = valA + valB;
      layer3[i] = valA + valB + valC;
      totalCurve[i] = layer3[i];

      const capacityAtX = xPct < q2BoundaryPct ? preQ2Capacity : postQ2Capacity;
      capacitySeries[i] = capacityAtX;

      const overCapacity = Math.max(0, totalCurve[i] - capacityAtX - breachBuffer);
      const periodRevenueBase = (valA * 2.2) + (valB * 2.8) + (valC * 3.6);
      const timeValueFactor = 1.8 - (xPct * 1.2);
      const compressionAccelerationBonus = 1 + (compressionLevel * (1 - xPct) * 0.25);
      const timeAdjustedRevenueBase = periodRevenueBase * timeValueFactor * compressionAccelerationBonus;
      const revenuePerUnitLoad = timeAdjustedRevenueBase / Math.max(totalCurve[i], 1);
      const slaPenalty = overCapacity * revenuePerUnitLoad * 4;
      const periodRevenue = Math.max(0, timeAdjustedRevenueBase - slaPenalty);

      runningRevenue += periodRevenue;
      revenueCumulative[i] = runningRevenue;
    }

    if (!revenueBaselineRef.current) {
      revenueBaselineRef.current = [...revenueCumulative];
    }
    const baselineFinalRevenue = revenueBaselineRef.current
      ? revenueBaselineRef.current[steps - 1]
      : revenueCumulative[steps - 1];
    const revenueLeakAmount = Math.max(0, baselineFinalRevenue - revenueCumulative[steps - 1]);
    const revenueChangePct = baselineFinalRevenue > 0
      ? ((revenueCumulative[steps - 1] - baselineFinalRevenue) / baselineFinalRevenue) * 100
      : 0;

    let breached = false;
    for (let i = 0; i < steps; i += 1) {
      if (totalCurve[i] > capacitySeries[i] + breachBuffer) {
        breached = true;
        break;
      }
    }

    const drawArea = (baseLayer, topLayer, color) => {
      capacityCtx.beginPath();
      capacityCtx.moveTo(0, height - paddingBottom);
      for (let i = 0; i < steps; i += 1) {
        const x = (i / (steps - 1)) * width;
        const y = height - paddingBottom - (topLayer[i] * (chartHeight / 100));
        capacityCtx.lineTo(x, y);
      }
      for (let i = steps - 1; i >= 0; i -= 1) {
        const x = (i / (steps - 1)) * width;
        const y = height - paddingBottom - (baseLayer[i] * (chartHeight / 100));
        capacityCtx.lineTo(x, y);
      }
      capacityCtx.closePath();
      capacityCtx.fillStyle = color;
      capacityCtx.fill();
    };

    capacityCtx.clearRect(0, 0, width, height);

    drawArea(new Array(steps).fill(0), layer1, '#e0e6ed');
    drawArea(layer1, layer2, '#a0b2c6');
    drawArea(layer2, layer3, breached ? 'rgba(0, 102, 204, 0.75)' : 'rgba(0, 102, 204, 0.5)');

    const q2X = width * q2BoundaryPct;
    const preQ2ThresholdY = height - paddingBottom - (preQ2Capacity * (chartHeight / 100));
    const postQ2ThresholdY = height - paddingBottom - (postQ2Capacity * (chartHeight / 100));

    capacityCtx.beginPath();
    capacityCtx.setLineDash([4, 4]);
    capacityCtx.moveTo(0, preQ2ThresholdY);
    capacityCtx.lineTo(q2X, preQ2ThresholdY);
    capacityCtx.lineTo(q2X, postQ2ThresholdY);
    capacityCtx.lineTo(width, postQ2ThresholdY);
    capacityCtx.lineWidth = 2;
    capacityCtx.strokeStyle = breached ? '#d9534f' : '#16a34a';
    capacityCtx.stroke();
    capacityCtx.setLineDash([]);

    capacityCtx.font = 'bold 12px sans-serif';
    capacityCtx.fillStyle = breached ? '#d9534f' : '#15803d';
    capacityCtx.fillText('MAX CAPACITY', 12, preQ2ThresholdY - 8);

    if (breached) {
      capacityCtx.fillStyle = '#d9534f';
      capacityCtx.fillText('SLA RISK', width - 55, postQ2ThresholdY - 6);
    }

    capacityCtx.beginPath();
    capacityCtx.moveTo(0, height - paddingBottom);
    capacityCtx.lineTo(width, height - paddingBottom);
    capacityCtx.lineWidth = 1;
    capacityCtx.strokeStyle = '#ccc';
    capacityCtx.stroke();

    capacityCtx.font = '9px sans-serif';
    capacityCtx.fillStyle = '#888';
    capacityCtx.fillText('Q1', 10, height - 10);
    capacityCtx.fillText('Q2', width * 0.33, height - 10);
    capacityCtx.fillText('Q3', width * 0.66, height - 10);
    capacityCtx.fillText('Q4', width - 25, height - 10);

    const revenueWidth = revenueCanvas.width;
    const revenueHeight = revenueCanvas.height;
    const revenuePaddingBottom = 25;
    const revenuePaddingTop = 25;
    const revenueChartHeight = revenueHeight - revenuePaddingBottom - revenuePaddingTop;
    revenueAxisMaxRef.current = Math.max(revenueAxisMaxRef.current, revenueCumulative[steps - 1]);
    const maxRevenue = Math.max(revenueAxisMaxRef.current, 1);

    revenueCtx.clearRect(0, 0, revenueWidth, revenueHeight);

    if (revenueBaselineRef.current) {
      revenueCtx.beginPath();
      for (let i = 0; i < steps; i += 1) {
        const x = (i / (steps - 1)) * revenueWidth;
        const y = revenueHeight - revenuePaddingBottom - ((revenueBaselineRef.current[i] / maxRevenue) * revenueChartHeight);
        if (i === 0) {
          revenueCtx.moveTo(x, y);
        } else {
          revenueCtx.lineTo(x, y);
        }
      }
      revenueCtx.setLineDash([6, 4]);
      revenueCtx.lineWidth = 2;
      revenueCtx.strokeStyle = '#9ca3af';
      revenueCtx.stroke();
      revenueCtx.setLineDash([]);
    }

    revenueCtx.beginPath();
    for (let i = 0; i < steps; i += 1) {
      const x = (i / (steps - 1)) * revenueWidth;
      const y = revenueHeight - revenuePaddingBottom - ((revenueCumulative[i] / maxRevenue) * revenueChartHeight);
      if (i === 0) {
        revenueCtx.moveTo(x, y);
      } else {
        revenueCtx.lineTo(x, y);
      }
    }
    revenueCtx.lineTo(revenueWidth, revenueHeight - revenuePaddingBottom);
    revenueCtx.lineTo(0, revenueHeight - revenuePaddingBottom);
    revenueCtx.closePath();
    revenueCtx.fillStyle = 'rgba(16, 185, 129, 0.18)';
    revenueCtx.fill();

    revenueCtx.beginPath();
    for (let i = 0; i < steps; i += 1) {
      const x = (i / (steps - 1)) * revenueWidth;
      const y = revenueHeight - revenuePaddingBottom - ((revenueCumulative[i] / maxRevenue) * revenueChartHeight);
      if (i === 0) {
        revenueCtx.moveTo(x, y);
      } else {
        revenueCtx.lineTo(x, y);
      }
    }
    revenueCtx.lineWidth = 2;
    revenueCtx.strokeStyle = '#059669';
    revenueCtx.stroke();

    revenueCtx.font = 'bold 15px sans-serif';
    const revenueDeltaLabel = `${Math.abs(revenueChangePct).toFixed(1)}% ${revenueChangePct >= 0 ? 'increase' : 'decrease'} in revenue`;
    revenueCtx.fillStyle = revenueChangePct >= 0 ? '#065f46' : '#b91c1c';
    const revenueSummaryY = revenuePaddingTop - 4;
    const revenueWarningY = revenueSummaryY + 14;
    revenueCtx.fillText(revenueDeltaLabel, 12, revenueSummaryY);

    if (breached) {
      revenueCtx.font = 'bold 12px sans-serif';
      revenueCtx.fillStyle = '#dc2626';
      const warningLabel = revenueLeakAmount > 0
        ? 'SLA Penalty'
        : 'Margin Penalty';
      revenueCtx.fillText(warningLabel, 12, revenueWarningY);
    }

    revenueCtx.font = 'bold 12px sans-serif';
    revenueCtx.fillStyle = '#047857';
    const revenueLabel = 'CUMULATIVE REVENUE';
    const revenueLabelWidth = revenueCtx.measureText(revenueLabel).width;
    revenueCtx.fillText(revenueLabel, revenueWidth - revenueLabelWidth - 12, revenueHeight - revenuePaddingBottom - 8);

    revenueCtx.beginPath();
    revenueCtx.moveTo(0, revenueHeight - revenuePaddingBottom);
    revenueCtx.lineTo(revenueWidth, revenueHeight - revenuePaddingBottom);
    revenueCtx.lineWidth = 1;
    revenueCtx.strokeStyle = '#ccc';
    revenueCtx.stroke();

    revenueCtx.font = '9px sans-serif';
    revenueCtx.fillStyle = '#888';
    revenueCtx.fillText('Q1', 10, revenueHeight - 10);
    revenueCtx.fillText('Q2', revenueWidth * 0.33, revenueHeight - 10);
    revenueCtx.fillText('Q3', revenueWidth * 0.66, revenueHeight - 10);
    revenueCtx.fillText('Q4', revenueWidth - 25, revenueHeight - 10);
  }, [scenarioCompression, scenarioCustA, scenarioCustB, scenarioCustC]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      plannerHoverReadyRef.current = true;
    }, 600);

    return () => {
      window.clearTimeout(timer);
    };
  }, []);

  useEffect(() => {
    if (!scenarioAutoplay) {
      return () => {};
    }

    plannerTimerRef.current = window.setInterval(() => {
      let nextIndex = plannerAutoIndexRef.current + plannerAutoDirectionRef.current;

      if (nextIndex >= SCENARIO_AUTOPLAY_LEVELS.length - 1) {
        nextIndex = SCENARIO_AUTOPLAY_LEVELS.length - 1;
        plannerAutoDirectionRef.current = -1;
      } else if (nextIndex <= 0) {
        nextIndex = 0;
        plannerAutoDirectionRef.current = 1;
      }

      plannerAutoIndexRef.current = nextIndex;
      plannerAutoValueRef.current = SCENARIO_AUTOPLAY_LEVELS[nextIndex];
      setScenarioCompression(SCENARIO_AUTOPLAY_LEVELS[nextIndex]);
    }, 650);

    return () => {
      window.clearInterval(plannerTimerRef.current);
    };
  }, [scenarioAutoplay]);

  useEffect(() => {
    const state = tickerState.current;

    const onScroll = () => {
      const now = window.scrollY;
      state.scrollBoost = Math.abs(now - state.lastScroll) * 0.15;
      state.lastScroll = now;
    };

    const animate = () => {
      if (tickerRef.current) {
        const tracks = tickerRef.current.querySelectorAll('.ticker-track');
        tracks.forEach((track, i) => {
          const speed = state.baseSpeeds[i] + state.scrollBoost;
          state.positions[i] -= speed;
          const setWidth = track.scrollWidth / 3;
          if (Math.abs(state.positions[i]) >= setWidth) {
            state.positions[i] += setWidth;
          }
          track.style.transform = `translateX(${state.positions[i]}px)`;
        });
        state.scrollBoost *= 0.92;

        if (!state.linkedCached) {
          state.linkedTickers = Array.from(tickerRef.current.querySelectorAll('.ticker-item[data-headline]'));
          state.linkedFeeds = Array.from(
            tickerRef.current.querySelectorAll('.sim-response[data-headline], .sim-preview[data-headline]'),
          );
          state.linkedCached = true;
        }

        const viewportWidth = window.innerWidth;
        const visibleByHeadline = new Map();
        state.linkedTickers.forEach((element) => {
          const rect = element.getBoundingClientRect();
          if (rect.right > 0 && rect.left < viewportWidth) {
            const headline = element.dataset.headline;
            const existing = visibleByHeadline.get(headline);
            const enteredDistance = viewportWidth - rect.right;
            if (!existing || enteredDistance < existing.entered) {
              visibleByHeadline.set(headline, { entered: enteredDistance });
            }
          }
        });

        if (!state.activeHL || !visibleByHeadline.has(state.activeHL)) {
          let bestHeadline = null;
          let leastEntered = Infinity;
          for (const [headline, info] of visibleByHeadline) {
            if (info.entered < leastEntered) {
              leastEntered = info.entered;
              bestHeadline = headline;
            }
          }
          state.activeHL = bestHeadline;
        }

        if (state.activeHL && state.activeHL !== activeHeadlineRef.current) {
          activeHeadlineRef.current = state.activeHL;
          setActiveHeadline(state.activeHL);
        }

        const forcedHeadline = hoveredHeadlineRef.current;
        const effectiveHeadline = forcedHeadline || state.activeHL;

        state.linkedTickers.forEach((element) => {
          const isMatch = element.dataset.headline === effectiveHeadline;
          element.classList.toggle('ticker-linked', !forcedHeadline && isMatch);
          element.classList.toggle('ticker-manual', !!forcedHeadline && isMatch);
        });
        state.linkedFeeds.forEach((element) => {
          const isMatch = element.dataset.headline === effectiveHeadline;
          element.classList.toggle('sim-linked', !forcedHeadline && isMatch);
          element.classList.toggle('sim-manual', !!forcedHeadline && isMatch);
        });
      }

      state.rafId = requestAnimationFrame(animate);
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    state.rafId = requestAnimationFrame(animate);

    return () => {
      window.removeEventListener('scroll', onScroll);
      cancelAnimationFrame(state.rafId);
    };
  }, []);

  const stopScenarioAutoplay = (source = 'manual') => {
    if (source === 'hover' && !plannerHoverReadyRef.current) {
      return;
    }

    if (source !== 'slider') {
      const highestIndex = SCENARIO_AUTOPLAY_LEVELS.length - 1;
      plannerAutoIndexRef.current = highestIndex;
      plannerAutoDirectionRef.current = -1;
      plannerAutoValueRef.current = SCENARIO_AUTOPLAY_LEVELS[highestIndex];
      setScenarioCompression(SCENARIO_AUTOPLAY_LEVELS[highestIndex]);
    }

    setScenarioAutoplay(false);
    window.clearInterval(plannerTimerRef.current);
  };

  const onCompressionChange = (event) => {
    const nextValue = parseFloat(event.target.value);
    stopScenarioAutoplay('slider');
    plannerAutoValueRef.current = nextValue;
    setScenarioCompression(nextValue);
  };

  const onCustomerToggle = (setter) => (event) => {
    stopScenarioAutoplay();
    setter(event.target.checked);
  };

  return (
    <div className="min-h-screen bg-white text-gray-900">
      <TopNav logo={logo} />

      <main>
        <style>{`
          :root {
            --veydra-accent: #3d1549;
            --veydra-accent-soft: #ede7f2;
            --veydra-ink: #1f2937;
            --veydra-purple: rgba(61, 21, 73, 1);
            --veydra-purple-mid: rgba(100, 58, 113, 1);
            --veydra-purple-soft: rgba(61, 21, 73, 0.24);
            --veydra-purple-faint: rgba(61, 21, 73, 0.08);
            --veydra-purple-rest: rgba(61, 21, 73, 0.05);
          }

          .veydra-section {
            max-width: 78rem;
            margin: 0 auto;
            padding-left: 1.25rem;
            padding-right: 1.25rem;
          }

          .veydra-wide-section {
            max-width: 88rem;
            margin: 0 auto;
            padding-left: 1rem;
            padding-right: 1rem;
          }

          .workflow-arrow {
            color: var(--veydra-accent);
            font-size: 1.2rem;
            line-height: 1;
          }

          .veydra-image-shell {
            position: relative;
            border-radius: 1rem;
            border: 1px solid #d8cae3;
            background: linear-gradient(135deg, #efe8ff 0%, #d8e4ff 45%, #eef2ff 100%);
            overflow: hidden;
          }

          .veydra-image-shell::before {
            content: '';
            position: absolute;
            inset: 0;
            background-image: radial-gradient(circle at 20% 20%, rgba(61, 21, 73, 0.14) 0, transparent 35%),
              radial-gradient(circle at 80% 80%, rgba(49, 113, 255, 0.18) 0, transparent 32%);
            pointer-events: none;
          }

          .veydra-image-frame {
            position: relative;
            margin: 1rem;
            border: 1px solid #c7cbe6;
            border-radius: 0.85rem;
            background: #ffffff;
            min-height: 16rem;
            display: flex;
            flex-direction: column;
            z-index: 1;
            box-shadow: 0 14px 28px rgba(41, 24, 58, 0.1);
          }

          .veydra-image-frame-header {
            display: flex;
            align-items: center;
            gap: 0.35rem;
            border-bottom: 1px solid #ececf8;
            padding: 0.65rem 0.8rem;
          }

          .veydra-image-dot {
            width: 0.55rem;
            height: 0.55rem;
            border-radius: 9999px;
            background: #b7bdd8;
          }

          .veydra-image-content {
            flex: 1;
            display: flex;
            align-items: center;
            justify-content: center;
            border: 2px dashed #d8cae3;
            margin: 1rem;
            border-radius: 0.75rem;
            color: #5f4a73;
            font-size: 0.82rem;
            letter-spacing: 0.08em;
            text-transform: uppercase;
            font-weight: 600;
            text-align: center;
            padding: 0.75rem;
          }

          .veydra-image-tag {
            position: absolute;
            top: 0.8rem;
            right: 0.8rem;
            border-radius: 9999px;
            padding: 0.2rem 0.55rem;
            background: rgba(61, 21, 73, 0.88);
            color: #fff;
            font-size: 0.65rem;
            font-weight: 700;
            letter-spacing: 0.07em;
            text-transform: uppercase;
            z-index: 2;
          }

          .veydra-placeholder-grid {
            display: grid;
            gap: 1.25rem;
          }

          .veydra-logo-marquee {
            position: relative;
            overflow: hidden;
            border-radius: 0.9rem;
            padding: 0.95rem 0;
          }

          .veydra-logo-marquee::before,
          .veydra-logo-marquee::after {
            content: '';
            position: absolute;
            top: 0;
            bottom: 0;
            width: 3.25rem;
            z-index: 2;
            pointer-events: none;
          }

          .veydra-logo-marquee::before {
            left: 0;
            background: linear-gradient(90deg, #ffffff 0%, rgba(255, 255, 255, 0) 100%);
          }

          .veydra-logo-marquee::after {
            right: 0;
            background: linear-gradient(270deg, #ffffff 0%, rgba(255, 255, 255, 0) 100%);
          }

          .veydra-logo-track {
            display: flex;
            align-items: center;
            gap: 0.85rem;
            width: max-content;
            animation: veydra-logo-scroll 34s linear infinite;
          }

          .veydra-logo-card {
            min-width: 10.5rem;
            border: 1px dashed #d6dbe7;
            border-radius: 0.7rem;
            background: #fff;
            padding: 0.62rem 0.9rem;
            display: flex;
            align-items: center;
            gap: 0.55rem;
          }

          .veydra-logo-mark {
            width: 1.25rem;
            height: 1.25rem;
            border-radius: 0.35rem;
            background: linear-gradient(135deg, #3d1549 0%, #6c4ea5 100%);
            opacity: 0.8;
            flex-shrink: 0;
          }

          .veydra-logo-name {
            color: #4b5563;
            font-size: 0.76rem;
            font-weight: 600;
            letter-spacing: 0.03em;
            white-space: nowrap;
          }

          .ticker-track {
            display: flex;
            width: max-content;
            will-change: transform;
          }

          .ticker-item {
            padding: 0.25rem 1.5rem;
            white-space: nowrap;
            font-family: 'Courier New', Courier, monospace;
            font-weight: 400;
            font-size: 0.82rem;
            color: #4b5563;
            letter-spacing: 0.01em;
            line-height: 1.2;
          }

          .ticker-item--bold {
            font-weight: 700;
            color: #111827;
            text-transform: uppercase;
          }

          .ticker-row-0 { font-size: 1.08rem; }
          .ticker-row-1 { font-size: 1.45rem; }
          .ticker-row-2 { font-size: 1rem; }
          .ticker-row-3 { font-size: 1.32rem; }
          .ticker-row-4 { font-size: 1.05rem; }

          @keyframes heartbeat-bg {
            0%, 100% { background-color: var(--veydra-purple-rest); }
            8% { background-color: var(--veydra-purple-soft); }
            18% { background-color: rgba(61, 21, 73, 0.06); }
            34% { background-color: rgba(100, 58, 113, 0.2); }
            52% { background-color: var(--veydra-purple-rest); }
          }

          .ticker-linked {
            animation: heartbeat-bg 2.4s cubic-bezier(0.22, 0.61, 0.36, 1) infinite;
            border-radius: 3px;
          }

          .ticker-manual {
            background-color: var(--veydra-purple-soft);
            border-radius: 3px;
          }

          .sim-linked {
            animation: heartbeat-bg 2.4s cubic-bezier(0.22, 0.61, 0.36, 1) infinite;
            border-radius: 6px;
          }

          .sim-manual {
            background-color: var(--veydra-purple-soft);
            border-radius: 6px;
          }

          @keyframes heartbeat-border {
            0%, 100% {
              border-color: rgba(100, 58, 113, 0.7);
              box-shadow: 0 0 0 1px var(--veydra-purple-faint);
            }
            8% {
              border-color: rgba(61, 21, 73, 0.78);
              box-shadow: 0 0 0 3px var(--veydra-purple-soft);
            }
            18% {
              border-color: rgba(100, 58, 113, 0.7);
              box-shadow: 0 0 0 1px rgba(61, 21, 73, 0.1);
            }
            34% {
              border-color: rgba(61, 21, 73, 0.78);
              box-shadow: 0 0 0 3px rgba(100, 58, 113, 0.2);
            }
            52% {
              border-color: rgba(100, 58, 113, 0.7);
              box-shadow: 0 0 0 1px var(--veydra-purple-faint);
            }
          }

          .sim-preview {
            border-color: rgba(100, 58, 113, 0.7);
          }

          .sim-preview.sim-linked {
            animation: heartbeat-border 2.4s cubic-bezier(0.22, 0.61, 0.36, 1) infinite;
          }

          .sim-preview.sim-manual {
            border-color: rgba(61, 21, 73, 0.78);
            box-shadow: 0 0 0 3px rgba(100, 58, 113, 0.22);
          }

          @keyframes veydra-logo-scroll {
            from { transform: translateX(0); }
            to { transform: translateX(-50%); }
          }

          @media (min-width: 768px) {
            .veydra-section {
              padding-left: 2rem;
              padding-right: 2rem;
            }

            .veydra-wide-section {
              padding-left: 2rem;
              padding-right: 2rem;
            }

            .veydra-image-frame {
              min-height: 21rem;
            }

            .veydra-logo-card {
              min-width: 12.5rem;
            }

            .veydra-placeholder-grid {
              grid-template-columns: repeat(2, minmax(0, 1fr));
            }
          }
        `}</style>

        <section className="pt-20 md:pt-24 pb-16 md:pb-20">
          <div className="max-w-7xl mx-auto px-6 md:px-16 lg:px-24">
            <p className="text-xs sm:text-sm font-semibold uppercase tracking-[0.16em] text-[#3d1549] mb-4">
              AI-Native Simulation Platform
            </p>
            <h1 className="text-5xl sm:text-6xl md:text-7xl font-semibold tracking-tight leading-[1.02] max-w-4xl">
              Build simulation models at the speed of thought.
            </h1>
            <p className="mt-5 text-2xl sm:text-3xl text-gray-800 leading-relaxed max-w-4xl">
              Run instant what-if scenarios, test strategies, and understand consequences before acting.
            </p>

            <div className="mt-9 flex flex-col sm:flex-row gap-3 sm:gap-4">
              <a
                href={APP_MODEL_NEW_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex justify-center bg-gray-900 text-white px-7 py-3 rounded-md text-sm font-medium hover:bg-gray-800 transition-colors"
              >
                Start Building
              </a>
              <Link
                to="/book-demo"
                className="inline-flex justify-center border border-gray-300 bg-white text-gray-900 px-7 py-3 rounded-md text-sm font-medium hover:bg-gray-50 transition-colors"
              >
                Book a Demo
              </Link>
            </div>

            <div className="mt-12 mb-14 md:mb-20 border border-gray-200 rounded-2xl bg-white overflow-hidden">
              <div className="grid grid-cols-1 md:grid-cols-4">
                {workflowSteps.map((step, index) => (
                  <div key={step.title} className="p-5 md:p-6 border-b md:border-b-0 md:border-r border-gray-200 last:border-r-0">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.15em] text-[#3d1549] mb-2">
                      Step {index + 1}
                    </p>
                    <p className="text-lg font-semibold text-gray-900">{step.title}</p>
                    <p className="mt-2 text-sm text-gray-600 leading-6">{step.detail}</p>
                    {index < workflowSteps.length - 1 && (
                      <p className="workflow-arrow mt-4 md:hidden" aria-hidden="true">↓</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="veydra-wide-section mt-0">
            <div className="veydra-image-shell">
              <span className="veydra-image-tag">Hero Preview</span>
              <div className="veydra-image-frame overflow-hidden min-h-0">
                <div className="veydra-image-frame-header" aria-hidden="true">
                  <span className="veydra-image-dot" />
                  <span className="veydra-image-dot" />
                  <span className="veydra-image-dot" />
                </div>
                <img
                  src="/preview-3.png"
                  alt="Product hero interface preview"
                  className="w-full h-auto object-contain block"
                />
              </div>
            </div>
          </div>
        </section>

        <section className="max-w-7xl mx-auto px-6 md:px-16 lg:px-24 pb-10 md:pb-14">
          <p className="text-base sm:text-lg text-gray-700 leading-relaxed max-w-4xl mb-4">
            Veydra combines causal simulation, agentic AI, and native Python to help leaders, analysts,
            and educators build decision models, test strategies, and understand consequences before acting.
          </p>
          <p className="text-sm sm:text-base text-gray-600 mb-4">
            Leaders, analysts, educators, students, and beta users from institutions like the following:
          </p>
          <div className="veydra-logo-marquee" aria-label="Auto-scrolling partner logo placeholders">
            <div className="veydra-logo-track">
              {[...partnerPlaceholders, ...partnerPlaceholders].map((label, index) => (
                <div key={`${label}-${index}`} className="veydra-logo-card">
                  <span className="veydra-logo-mark" aria-hidden="true" />
                  <span className="veydra-logo-name">{label}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="max-w-7xl mx-auto px-6 md:px-16 lg:px-24 py-16 md:py-20 border-t border-gray-100">
          <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight mb-4">Why simulation?</h2>
          <p className="text-lg text-gray-700 max-w-3xl leading-relaxed">
            Most analytics tells you what already happened. Machine learning predicts what may happen when conditions are similar to past observations. Simulation helps
            you forecast from the structure of the system itself, even when historical data is limited or missing.
          </p>

          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
            <div className="rounded-xl border border-gray-200 p-5">
              <div className="flex items-start gap-4">
                <div className="w-16 h-16 text-slate-700 flex items-center justify-center shrink-0" aria-hidden="true">
                  <svg viewBox="0 0 24 24" className="w-10 h-10" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="4" y1="20" x2="20" y2="20" />
                    <rect x="6" y="11" width="3.5" height="7" rx="0.8" />
                    <rect x="11" y="8" width="3.5" height="10" rx="0.8" />
                    <rect x="16" y="5" width="3.5" height="13" rx="0.8" />
                  </svg>
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.14em] text-gray-500">Analytics</p>
                  <p className="mt-2 text-base text-gray-700">Looks backward at historical outcomes.</p>
                </div>
              </div>
            </div>
            <div className="rounded-xl border border-gray-200 p-5">
              <div className="flex items-start gap-4">
                <div className="w-16 h-16 text-slate-700 flex items-center justify-center shrink-0" aria-hidden="true">
                  <svg viewBox="0 0 24 24" className="w-10 h-10" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="4 16 8 13 11 14.5 16 9 20 11" />
                    <line x1="4" y1="20" x2="20" y2="20" />
                    <line x1="4" y1="5" x2="4" y2="20" />
                    <circle cx="8" cy="13" r="1" fill="currentColor" stroke="none" />
                    <circle cx="11" cy="14.5" r="1" fill="currentColor" stroke="none" />
                    <circle cx="16" cy="9" r="1" fill="currentColor" stroke="none" />
                  </svg>
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.14em] text-gray-500">Machine Learning</p>
                  <p className="mt-2 text-base text-gray-700">Estimates likely future outcomes from patterns of historical observations.</p>
                </div>
              </div>
            </div>
            <div className="rounded-xl border border-[#d8cae3] bg-[#f7f3fa] p-5">
              <div className="flex items-start gap-4">
                <div className="w-16 h-16 text-slate-700 flex items-center justify-center shrink-0" aria-hidden="true">
                  <svg viewBox="0 0 24 24" className="w-10 h-10" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="7.5" />
                    <circle cx="12" cy="12" r="4" />
                    <circle cx="12" cy="12" r="1.4" fill="currentColor" stroke="none" />
                  </svg>
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.14em] text-[#3d1549]">Simulation</p>
                  <p className="mt-2 text-base text-gray-800">Uses system structure to forecast behavior and decision tradeoffs.</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-12 md:py-16 border-t border-gray-100 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6 md:px-16 lg:px-24">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8 items-center">
            <div className="order-2 lg:order-2">
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[#3d1549] mb-3">Live Demo</p>
              <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight mb-4">Try a live model instantly.</h2>
              <p className="text-lg text-gray-700 leading-relaxed max-w-2xl mb-6">
                Open a working simulation, test assumptions, and explore scenario outcomes without setup.
              </p>
              <a
                href="https://app.veydra.io/model/5767459395076096/start?guided=true"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex justify-center bg-gray-900 text-white px-6 py-3 rounded-md text-sm font-medium hover:bg-gray-800 transition-colors"
              >
                View Live Interactive Model
              </a>
            </div>

            <a
              href="https://app.veydra.io/model/5767459395076096/start?guided=true"
              target="_blank"
              rel="noopener noreferrer"
              className="veydra-image-shell order-1 lg:order-1 block"
            >
              <span className="veydra-image-tag">Live Demo</span>
              <div className="veydra-image-frame min-h-0">
                <div className="veydra-image-frame-header" aria-hidden="true">
                  <span className="veydra-image-dot" />
                  <span className="veydra-image-dot" />
                  <span className="veydra-image-dot" />
                </div>
                <img
                  src="/preview-scenarios.png"
                  alt="Live demo model preview"
                  className="w-full h-auto block"
                />
              </div>
            </a>
            </div>
          </div>
        </section>

        <section className="max-w-7xl mx-auto px-6 md:px-16 lg:px-24 py-16 md:py-20 border-t border-gray-100">
          <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight mb-8">Why Veydra?</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
            {veydraCards.map((card) => (
              <article key={card.title} className="rounded-xl border border-gray-200 p-6">
                <h3 className="text-xl font-semibold text-gray-900 tracking-tight">{card.title}</h3>
                <p className="mt-3 text-gray-700 leading-7">{card.text}</p>
              </article>
            ))}
          </div>
        </section>

        <section ref={tickerRef} className="py-14 sm:py-16 bg-[#f7f8fb] border-y border-gray-200 overflow-hidden">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 md:px-0">
            <div className="max-w-3xl mb-8 sm:mb-10">
              <p className="text-xs sm:text-sm font-semibold uppercase tracking-[0.14em] text-[#643a71] mb-2">
                Live Events
              </p>
              <h2 className="text-3xl sm:text-4xl font-semibold text-gray-900 mb-3 tracking-tight">
                Real headlines. Real impact.
              </h2>
              <p className="text-gray-600 text-base sm:text-lg">
                Global events ripple through your simulation in real time.
              </p>
            </div>
          </div>

          <div className="w-full mb-10">
            <div className="flex flex-col gap-[0.35rem]">
              {liveTickerRows.map((row, rowIdx) => {
                return (
                  <div key={`ticker-row-${rowIdx}`} className="overflow-hidden w-full">
                    <div className="ticker-track">
                      {[...row, ...row, ...row].map((headline, idx) => {
                        const boldClass = (rowIdx * 7 + (idx % row.length)) % 11 === 0 ? ' ticker-item--bold' : '';
                        return (
                          <span
                            key={`${rowIdx}-${idx}`}
                            className={`ticker-item ticker-row-${rowIdx}${boldClass}`}
                            style={{ cursor: 'pointer' }}
                            onClick={() => window.location.assign(`${appBaseUrl}/model/new?q=${encodeURIComponent(headline)}`)}
                            {...(feedHeadlines.has(headline) ? { 'data-headline': headline } : {})}
                          >
                            {headline}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="max-w-4xl mx-auto px-4 sm:px-6 md:px-0">
            <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_300px] gap-6 lg:gap-8 items-stretch">
              <div>
                <p className="text-[11px] sm:text-xs font-semibold uppercase tracking-[0.16em] text-gray-500 mb-3">
                  Simulation Response Examples
                </p>
                <div>
                  {liveFeed.map((item) => {
                    return (
                      <div
                        key={item.module}
                        data-headline={item.headline}
                        onMouseEnter={() => {
                          hoveredHeadlineRef.current = item.headline;
                          setHoveredHeadline(item.headline);
                        }}
                        onMouseLeave={() => {
                          hoveredHeadlineRef.current = null;
                          setHoveredHeadline(null);
                        }}
                        onClick={() => window.location.assign(`${appBaseUrl}/model/new?q=${encodeURIComponent(item.headline)}`)}
                        style={{ cursor: 'pointer' }}
                        className="sim-response flex items-center gap-3 px-2 py-2 border-b border-gray-200 hover:bg-[#f7f3fa] transition-colors"
                      >
                        <span className="text-[10px] sm:text-[11px] uppercase font-bold tracking-wide text-[#3d1549] bg-[#efe7f4] px-2 py-1 rounded">
                          {item.module}
                        </span>
                        <span className="text-sm text-gray-700">{item.effect}</span>
                      </div>
                    );
                  })}
                </div>
              </div>

              <aside className="flex flex-col h-full mt-6 lg:mt-0">
                <p className="text-[11px] sm:text-xs font-semibold uppercase tracking-[0.16em] text-gray-500 mb-3">
                  Active Scenario Preview
                </p>
                <div
                  data-headline={hoveredHeadline || activeHeadline}
                  className="sim-preview sim-preview-card border-4 border-transparent rounded-lg overflow-hidden bg-white relative flex-1 min-h-[260px]"
                >
                  <img
                    src="/preview-3.png"
                    alt={`Preview for ${activePreview.headline}`}
                    className="w-full h-full object-cover"
                  />
                  <p className="absolute bottom-2 left-2 right-2 text-[11px] text-gray-700 bg-white/85 rounded px-2 py-1 leading-snug">
                    Linked to: <span className="font-medium text-gray-900">{activePreview.headline}</span>
                  </p>
                </div>
              </aside>
            </div>
          </div>
        </section>

        <section className="max-w-7xl mx-auto px-6 md:px-16 lg:px-24 py-16 md:py-20 border-t border-gray-100">
          <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight mb-8">How it works</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            <article className="rounded-2xl border border-gray-200 p-6">
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[#3d1549] mb-4">01</p>
              <h3 className="text-xl font-semibold mb-3">Describe your system</h3>
              <p className="text-gray-700 leading-7">Capture goals, constraints, and key feedback loops in natural language.</p>
            </article>
            <article className="rounded-2xl border border-gray-200 p-6">
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[#3d1549] mb-4">02</p>
              <h3 className="text-xl font-semibold mb-3">AI builds the initial model</h3>
              <p className="text-gray-700 leading-7">Generate model structure and editable equations you can inspect and refine.</p>
            </article>
            <article className="rounded-2xl border border-gray-200 p-6">
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[#3d1549] mb-4">03</p>
              <h3 className="text-xl font-semibold mb-3">Explore scenarios</h3>
              <p className="text-gray-700 leading-7">Compare assumptions, stress test decisions, and share model-backed choices.</p>
            </article>
          </div>

          <div className="veydra-placeholder-grid mt-10">
            <div className="veydra-image-shell">
              <span className="veydra-image-tag">Feature 01</span>
              <div className="veydra-image-frame overflow-hidden min-h-0 aspect-[498/430]">
                <div className="veydra-image-frame-header" aria-hidden="true">
                  <span className="veydra-image-dot" />
                  <span className="veydra-image-dot" />
                  <span className="veydra-image-dot" />
                </div>
                <img
                  src="/new-model.jpg"
                  alt="Feature preview showing guided new model creation"
                  className="w-full flex-1 object-contain block bg-white"
                />
              </div>
            </div>

            <div className="veydra-image-shell">
              <span className="veydra-image-tag">Feature 02</span>
              <div className="veydra-image-frame overflow-hidden min-h-0 aspect-[498/430]">
                <div className="veydra-image-frame-header" aria-hidden="true">
                  <span className="veydra-image-dot" />
                  <span className="veydra-image-dot" />
                  <span className="veydra-image-dot" />
                </div>
                <img
                  src="/preview-2.jpg"
                  alt="Feature preview showing model editing workspace"
                  className="w-full h-full object-cover object-top block"
                />
              </div>
            </div>
          </div>
        </section>

        <section className="max-w-7xl mx-auto px-6 md:px-16 lg:px-24 py-16 md:py-20 border-t border-gray-100">
          <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight mb-8">Traditional vs modern simulation workflow</h2>
          <div className="overflow-hidden rounded-2xl border border-gray-200">
            <table className="w-full text-left text-sm sm:text-base">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 sm:px-6 py-4 font-semibold text-gray-900">Traditional workflow</th>
                  <th className="px-4 sm:px-6 py-4 font-semibold text-[#1f4f3f]">New workflow</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-t border-gray-100">
                  <td className="px-4 sm:px-6 py-4 text-gray-700">
                    <span className="text-red-600 font-semibold mr-2" aria-hidden="true">X</span>
                    One-way diagram-to-model flow.
                  </td>
                  <td className="px-4 sm:px-6 py-4 text-gray-900">
                    <span className="text-green-600 font-semibold mr-2" aria-hidden="true">✓</span>
                    Bidirectional code, text, and diagram sync.
                  </td>
                </tr>
                <tr className="border-t border-gray-100">
                  <td className="px-4 sm:px-6 py-4 text-gray-700">
                    <span className="text-red-600 font-semibold mr-2" aria-hidden="true">X</span>
                    Single-paradigm modeling.
                  </td>
                  <td className="px-4 sm:px-6 py-4 text-gray-900">
                    <span className="text-green-600 font-semibold mr-2" aria-hidden="true">✓</span>
                    SD + ABM + DES together.
                  </td>
                </tr>
                <tr className="border-t border-gray-100">
                  <td className="px-4 sm:px-6 py-4 text-gray-700">
                    <span className="text-red-600 font-semibold mr-2" aria-hidden="true">X</span>
                    Local runtime only.
                  </td>
                  <td className="px-4 sm:px-6 py-4 text-gray-900">
                    <span className="text-green-600 font-semibold mr-2" aria-hidden="true">✓</span>
                    Browser, cloud, and local runtime.
                  </td>
                </tr>
                <tr className="border-t border-gray-100">
                  <td className="px-4 sm:px-6 py-4 text-gray-700">
                    <span className="text-red-600 font-semibold mr-2" aria-hidden="true">X</span>
                    AI is add-on only.
                  </td>
                  <td className="px-4 sm:px-6 py-4 text-gray-900">
                    <span className="text-green-600 font-semibold mr-2" aria-hidden="true">✓</span>
                    Native, paradigm-aware AI.
                  </td>
                </tr>
                <tr className="border-t border-gray-100">
                  <td className="px-4 sm:px-6 py-4 text-gray-700">
                    <span className="text-red-600 font-semibold mr-2" aria-hidden="true">X</span>
                    Closed formats and lock-in.
                  </td>
                  <td className="px-4 sm:px-6 py-4 text-gray-900">
                    <span className="text-green-600 font-semibold mr-2" aria-hidden="true">✓</span>
                    Portable typed model contracts.
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        <section className="py-12 md:py-16 border-t border-gray-100 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6 md:px-16 lg:px-24">
            <div className="rounded-2xl border border-gray-200 bg-white p-6 md:p-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8 items-center">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[#3d1549] mb-3">Model Conversion</p>
                <h3 className="text-2xl sm:text-3xl font-semibold tracking-tight text-gray-900 mb-4">
                  Bring an existing system dynamics model and convert it.
                </h3>
                <p className="text-gray-700 leading-7 mb-5">
                  Import your current SD work, preserve core structure, and move into an editable Python workflow without starting from scratch.
                </p>
                <ul className="space-y-2 text-sm sm:text-base text-gray-700">
                  <li className="flex items-start"><span className="text-green-600 font-semibold mr-2" aria-hidden="true">✓</span>Keep stocks, flows, and auxiliaries intact</li>
                  <li className="flex items-start"><span className="text-green-600 font-semibold mr-2" aria-hidden="true">✓</span>Convert into code you can inspect and version</li>
                  <li className="flex items-start"><span className="text-green-600 font-semibold mr-2" aria-hidden="true">✓</span>Run scenarios immediately after conversion</li>
                </ul>
              </div>

              <a
                href="https://app.veydra.io/model/import-xmile"
                target="_blank"
                rel="noopener noreferrer"
                className="group relative rounded-xl border border-gray-200 overflow-hidden bg-gray-50 block"
              >
                <img
                  src="/upload-model.jpg"
                  alt="Model conversion workflow showing an existing system dynamics model transformed into an interactive simulation workspace"
                  className="w-full h-full object-cover transition-opacity duration-200 group-hover:opacity-20"
                />
                <div className="absolute inset-0 flex items-center justify-center opacity-0 transition-opacity duration-200 group-hover:opacity-100">
                  <span className="inline-flex justify-center bg-gray-900 text-white px-6 py-3 rounded-md text-sm font-medium hover:bg-gray-800 transition-colors">
                    Upload Existing Model
                  </span>
                </div>
              </a>
            </div>
            </div>
          </div>
        </section>

        <section className="max-w-7xl mx-auto px-6 md:px-16 lg:px-24 py-16 md:py-20 border-t border-gray-100">
          <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight mb-8">Built for people solving complex systems</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {applications.map((item) => (
              <Link
                key={item.title}
                to={item.to}
                className="rounded-xl border border-gray-200 p-5 hover:border-[#b79ac8] hover:bg-[#faf8fc] transition-colors"
              >
                <p className="font-medium text-gray-900">{item.title}</p>
              </Link>
            ))}
          </div>

          <div
            className="mt-10 bg-white p-4 rounded-lg shadow-[0_4px_12px_rgba(0,0,0,0.05)] border border-gray-100"
            onMouseEnter={() => stopScenarioAutoplay('hover')}
            onTouchStart={stopScenarioAutoplay}
          >
            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-4">
              <div className="text-left">
                <h4 className="m-0 mb-1 text-[#333] tracking-[0.05em] text-[14px] font-semibold">EXAMPLE CAPACITY PLANNING</h4>
                <p className="text-sm text-gray-700 font-medium m-0">See how a single compressed contract collapses a pipeline:</p>
              </div>

              <div className="w-full md:w-auto md:min-w-[560px]">
                <div className="flex flex-col md:flex-row md:items-start md:justify-end gap-3">
                  <div className="w-full md:w-[340px] order-2 md:order-1">
                    <div className="flex flex-col gap-1 md:flex-row md:items-center md:gap-3">
                      <span className="text-[11px] font-bold text-[#0066cc] whitespace-nowrap md:mb-0">Timeline Compression</span>
                      <input
                        type="range"
                        min="1"
                        max="2"
                        step="0.01"
                        value={scenarioCompression}
                        onChange={onCompressionChange}
                        className="w-full cursor-pointer accent-[#0066cc]"
                      />
                    </div>
                  </div>

                  <div className="flex items-center justify-start md:justify-end gap-3 text-[12px] whitespace-nowrap order-1 md:order-2">
                    <span className="text-gray-600 font-medium">Customers</span>
                    <label className="inline-flex items-center gap-1 text-gray-700">
                      <input type="checkbox" checked={scenarioCustA} onChange={onCustomerToggle(setScenarioCustA)} className="h-4 w-4 accent-[#2563eb]" />
                      <span>A</span>
                    </label>
                    <label className="inline-flex items-center gap-1 text-gray-700">
                      <input type="checkbox" checked={scenarioCustB} onChange={onCustomerToggle(setScenarioCustB)} className="h-4 w-4 accent-[#2563eb]" />
                      <span>B</span>
                    </label>
                    <label className="inline-flex items-center gap-1 text-gray-900 font-semibold">
                      <input type="checkbox" checked={scenarioCustC} onChange={onCustomerToggle(setScenarioCustC)} className="h-4 w-4 accent-[#0066cc]" />
                      <span>C</span>
                    </label>
                  </div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="rounded-md border border-gray-100 p-2">
                <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-2">Capacity Volume</p>
                <canvas
                  ref={plannerCanvasRef}
                  width="520"
                  height="240"
                  className="w-full h-auto block"
                  aria-label="Capacity scenario planner chart"
                />
              </div>

              <div className="rounded-md border border-gray-100 p-2">
                <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-2">Cumulative Revenue</p>
                <canvas
                  ref={revenueCanvasRef}
                  width="520"
                  height="240"
                  className="w-full h-auto block"
                  aria-label="Cumulative revenue chart"
                />
              </div>
            </div>
          </div>
        </section>

        <section className="py-16 md:py-20 border-t border-gray-100 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6 md:px-16 lg:px-24">
            <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight mb-8">Two ways to work with Veydra</h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
              <article className="rounded-2xl border border-gray-200 bg-white p-7">
                <p className="text-xs font-semibold uppercase tracking-[0.15em] text-gray-500 mb-3">Build it yourself</p>
                <h3 className="text-2xl font-semibold mb-3 tracking-tight">For analysts, researchers, developers, and educators</h3>
                <p className="text-gray-700 leading-7 mb-6">Start in a free workspace and build simulation models directly in native Python.</p>
                <a
                  href={`${appBaseUrl}/model/new`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex bg-gray-900 text-white px-6 py-3 rounded-md text-sm font-medium hover:bg-gray-800 transition-colors"
                >
                  Start Building
                </a>
              </article>

              <article className="rounded-2xl border border-[#d8cae3] bg-[#f7f3fa] p-7">
                <p className="text-xs font-semibold uppercase tracking-[0.15em] text-[#3d1549] mb-3">Partner with us</p>
                <h3 className="text-2xl font-semibold mb-3 tracking-tight">For enterprise teams navigating strategic decisions</h3>
                <p className="text-gray-700 leading-7 mb-6">Work with our team on decision intelligence consulting, custom simulation models, and digital twins.</p>
                <Link
                  to="/book-demo"
                  className="inline-flex bg-[#3d1549] text-white px-6 py-3 rounded-md text-sm font-medium hover:bg-[#31113b] transition-colors"
                >
                  Talk to an Expert
                </Link>
              </article>
            </div>
          </div>
        </section>

        <section className="py-18 md:py-20 mt-4 bg-gray-900 text-white">
          <div className="veydra-section text-center">
            <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight">Better decisions begin with better models.</h2>
            <p className="mt-4 text-lg text-gray-200">Stop guessing. Start simulating.</p>
            <div className="mt-8 flex flex-col sm:flex-row justify-center gap-3 sm:gap-4">
              <a
                href={`${appBaseUrl}/model/new`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex justify-center bg-white text-gray-900 px-7 py-3 rounded-md text-sm font-medium hover:bg-gray-100 transition-colors"
              >
                Start Building
              </a>
              <Link
                to="/book-demo"
                className="inline-flex justify-center border border-gray-500 text-white px-7 py-3 rounded-md text-sm font-medium hover:bg-gray-800 transition-colors"
              >
                Talk to an Expert
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}

export default LoginScreen;
